import { Cause, Effect, Exit, Layer, Option, Ref, Schema } from "effect";
import { StationRevision } from "@workerstation/shared/station/http";
import { workspaceInvalid } from "@workerstation/shared/errors/workspace-http";
import { StationApi } from "../../api/station/service.ts";
import type { StationClientFailure, WorkspaceWrite } from "../../api/station/model.ts";
import { AcceptedWork } from "../accepted-work.ts";
import { PersistenceOrder } from "./persistence-order.ts";
import { StationPersistence } from "./persistence-service.ts";
import { WorkspaceDraft, WorkspaceTarget, WorkspaceVersionObservation, workspacePersistenceKey, type WorkspaceVersion } from "./persistence-model.ts";

const target = (value: WorkspaceTarget) => Schema.decodeUnknownEffect(WorkspaceTarget)(value).pipe(Effect.mapError(() => workspaceInvalid("Invalid workerspace persistence target.")));
const validRevision = Schema.is(StationRevision);

/** Acquire this layer in root resources, whose close follows accepted work.
 * A component's shorter scope must not close the late-failure receipt. */
export const makeStationPersistence = Effect.fn("Frontend.StationPersistence.make")(function* () {
  const api = yield* StationApi, accepted = yield* AcceptedWork, order = yield* PersistenceOrder;
  const versions = yield* Ref.make<ReadonlyMap<string, WorkspaceVersion>>(new Map());
  const generations = yield* Ref.make<ReadonlyMap<string, number>>(new Map());
  const privateFailures = yield* Ref.make<readonly Cause.Reason<never>[]>([]);
  yield* Effect.addFinalizer(() => Ref.get(privateFailures).pipe(Effect.flatMap(reasons => reasons.length ? Effect.failCause(Cause.fromReasons(reasons)) : Effect.void)));
  const update = (key: string, change: (current: WorkspaceVersion) => WorkspaceVersion) => Ref.update(versions, current => {
    const next = change(current.get(key) ?? {}), updated = new Map(current);
    if (next.legacyUpdatedAt === undefined && next.repository === undefined) updated.delete(key);
    else updated.set(key, next);
    return updated;
  });
  const commit = <A>(key: string, operation: Effect.Effect<A, StationClientFailure>) => Effect.gen(function* () {
    const result = yield* accepted.join(Effect.gen(function* () {
      const exit = yield* Effect.exit(order.run(key, operation));
      if (Exit.isFailure(exit)) yield* Ref.update(privateFailures, current => [...current, ...exit.cause.reasons.filter(reason => reason._tag === "Die")]);
      return exit;
    }));
    return yield* result;
  });
  return StationPersistence.of({
    observeVersion: Effect.fn("Frontend.StationPersistence.observeVersion")(function* (identity, observation, preserve = false) {
      const prepared = yield* target(identity);
      const seen = yield* Schema.decodeUnknownEffect(WorkspaceVersionObservation)(observation).pipe(Effect.mapError(() => workspaceInvalid("Invalid workerspace version metadata.")));
      yield* update(workspacePersistenceKey(prepared), current => {
        const next = { ...current };
        if (validRevision(seen.updatedAt)) next.legacyUpdatedAt = seen.updatedAt;
        else if (!preserve) delete next.legacyUpdatedAt;
        if (validRevision(seen.refRevision)) next.repository = { revision: seen.refRevision, ...(seen.commitId === undefined ? {} : { commitId: seen.commitId }) };
        else if (!preserve) delete next.repository;
        return next;
      });
    }),
    copyRepositoryVersion: Effect.fn("Frontend.StationPersistence.copyRepositoryVersion")(function* (from, to) {
      const source = yield* target(from), destination = yield* target(to);
      const repository = yield* Ref.get(versions).pipe(Effect.map(current => Option.fromNullishOr(current.get(workspacePersistenceKey(source))?.repository)));
      yield* Option.match(repository, { onNone: () => Effect.void, onSome: value => update(workspacePersistenceKey(destination), current => ({ ...current, repository: { ...value } })) });
    }),
    discardRepositoryVersion: Effect.fn("Frontend.StationPersistence.discardRepositoryVersion")(function* (identity) {
      const prepared = yield* target(identity);
      yield* update(workspacePersistenceKey(prepared), current => { const { repository, ...remaining } = current; return remaining; });
    }),
    version: Effect.fn("Frontend.StationPersistence.version")(function* (identity) {
      const prepared = yield* target(identity);
      return yield* Ref.get(versions).pipe(Effect.map(current => Option.fromNullishOr(current.get(workspacePersistenceKey(prepared)))));
    }),
    generation: Effect.fn("Frontend.StationPersistence.generation")(function* (identity) {
      const prepared = yield* target(identity);
      return yield* Ref.get(generations).pipe(Effect.map(current => current.get(workspacePersistenceKey(prepared)) ?? 0));
    }),
    isWriting: Effect.fn("Frontend.StationPersistence.isWriting")(function* (identity) {
      const prepared = yield* target(identity);
      return yield* order.contains(workspacePersistenceKey(prepared));
    }),
    save: Effect.fn("Frontend.StationPersistence.save")(function* (identity, input) {
      const prepared = yield* target(identity);
      const draft = yield* Schema.decodeUnknownEffect(WorkspaceDraft)(input).pipe(Effect.mapError(() => workspaceInvalid("Invalid workerspace draft.")));
      const key = workspacePersistenceKey(prepared);
      return yield* commit(key, Effect.gen(function* () {
        const version = yield* Ref.get(versions).pipe(Effect.map(current => current.get(key)));
        const repository = version?.repository;
        const payload: WorkspaceWrite = { files: draft.files, openTabs: draft.openTabs, ...(draft.currentFile === undefined ? {} : { currentFile: draft.currentFile }), folders: draft.folders, stationId: prepared.stationId,
          ...(repository ? { expectedRevision: repository.revision, ...(repository.commitId ? { expectedCommitId: repository.commitId } : {}) } : version?.legacyUpdatedAt === undefined ? {} : { expectedUpdatedAt: version.legacyUpdatedAt }) };
        const result = yield* api.updateWorkspace(prepared.workspaceId, payload, prepared.stationId);
        yield* update(key, current => repository && "revision" in result
          ? { ...current, repository: { revision: result.revision, commitId: result.commitId } }
          : { ...current, legacyUpdatedAt: result.updatedAt });
        yield* Ref.update(generations, current => new Map(current).set(key, (current.get(key) ?? 0) + 1));
        return result;
      }));
    })
  });
});
export const StationPersistenceLive = Layer.effect(StationPersistence, makeStationPersistence());
